import {fetchAuthSession} from 'aws-amplify/auth'
import {getConfig} from './config'
import {getLogger} from './logging'
import {BluemoonPrefillData} from '../types/app-types'

// declare BluemoonElements as a global variable
declare var BluemoonElements: any

interface BluemoonAccess {
  accessToken: string
  propertyId: string
}

const {bluemoonLeaseApiBase, residentPortalApiUrl} = getConfig()

const logger = getLogger('bluemoon')

/**
 * Get the access token and internal property id
 * @param propertyCode The Apaleo property code (ie. WHYDCPENN)
 */
async function getBluemoonAccess(
  propertyCode: string
): Promise<BluemoonAccess> {
  const requestUrl = `${residentPortalApiUrl}/bluemoonAuth?propertyId=${propertyCode}`
  const session = await fetchAuthSession()
  const token = session.tokens.idToken.toString()
  const response = await fetch(requestUrl, {
    method: 'GET',
    mode: 'cors',
    headers: new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': token
    })
  })
  const {access_token, property_id} = await response.json()
  return {
    accessToken: access_token,
    propertyId: property_id
  }
}

/**
 * Fetches lease PDF document from Bluemoon API as binary data
 * @param accessToken The access token
 * @param leaseSignatureId The lease signature ID
 */
async function getLeaseBlob(accessToken: string, leaseSignatureId: string) {
  const requestUrl = `${bluemoonLeaseApiBase}/${leaseSignatureId}`
  const response = await fetch(requestUrl, {
    method: 'GET',
    mode: 'cors',
    headers: new Headers({
      'Authorization': `Bearer ${accessToken}`
    })
  })
  if (response.status !== 200) {
    logger.logError('Bluemoon lease download request failed', {
      accessToken,
      leaseSignatureId,
      response
    })
    throw Error('Failed to download lease document')
  }
  const leaseData = await response.blob()
  return new Blob([leaseData], {type: 'application/pdf'})
}

/**
 * Performs a programmatic download of the lease PDF file
 * @param propertyId The property ID
 * @param leaseSignatureId The lease signature ID
 */
export async function downloadLease(
  propertyCode: string,
  leaseSignatureId: string
): Promise<void> {
  try {
    // get the access token for the property
    const {accessToken} = await getBluemoonAccess(propertyCode)

    // create a download URL
    const leaseBlob = await getLeaseBlob(accessToken, leaseSignatureId)
    const downloadUrl = URL.createObjectURL(leaseBlob)

    // create download link element and append it to the DOM
    const downloadLink = document.createElement('a')
    downloadLink.href = downloadUrl
    downloadLink.setAttribute('download', `lease-${leaseSignatureId}.pdf`)
    document.body.appendChild(downloadLink)

    // click the download link programmatically
    downloadLink.click()

    // remove the download link
    downloadLink.parentNode.removeChild(downloadLink)

    // clean up the download URL (prevent memory leaks)
    URL.revokeObjectURL(downloadUrl)
  } catch (e) {
    logger.logError('Failed to download lease document', {
      error: e
    })
  }
}

/**
 * Initialize bluemoon forms library, dynamically adding a script element
 * to load the library
 */
export function initBluemoonForms(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // do not load again if bluemoon script has already been loaded
    if ((window as any)['BluemoonElements']) {
      resolve()
    }

    try {
      console.log('Loading bluemoon forms library...')

      // dynamically inject a script tag to load bluemoon forms library
      const scriptSrc = 'https://cdn.bluemoonforms.com/launcher.js'
      const script = document.createElement('script')
      script.setAttribute('src', scriptSrc)
      script.onload = () => {
        console.log('Bluemoon forms library loaded')
        resolve()
      }
      script.onerror = () => {
        console.error('Failed to load Bluemoon forms library')
        reject()
      }
      document.head.appendChild(script)
    } catch (e) {
      console.error('Failed to load Bluemoon forms library')
      reject()
    }
  })
}

/**
 * Render lease application forms
 * @param propertyCode
 * @param containerElementId
 * @param applicationId
 * @param prefillData
 */
export async function renderLeaseApplication(
  propertyCode: string,
  containerElementId: string,
  applicationId?: string,
  prefillData?: BluemoonPrefillData
): Promise<any> {
  try {
    logger.logInfo('Initializing bluemoon forms library...')
    await initBluemoonForms()
    const {accessToken, propertyId} = await getBluemoonAccess(propertyCode)
    const bluemoonElements = new BluemoonElements(accessToken, propertyId)
    const applicationData = {
      applicant_first_name: prefillData?.firstName,
      applicant_last_name: prefillData?.lastName,
      current_email: prefillData?.email,
      apply_address: prefillData?.propertyAddress
    }

    // see: https://integrators.bluemoonforms.com/#/resources/markdown/web-components
    const params = {
      applicationId,
      applicationData,
      skipStartPage: true,
      openAllSteps: false
    }

    const frameOptions = {
      dynamicResize: true,
      maximumHeight: '700',
      height: '700'
    }

    logger.logInfo('Loading bluemoon lease application form into page...', {
      params,
      frameOptions
    })

    const element = bluemoonElements.load(
      containerElementId,
      'bm-rental-application',
      params,
      frameOptions
    )

    logger.logInfo('Rendering bluemoon lease application form complete')

    return element
  } catch (e) {
    console.error('Failed to render bluemoon lease application', e)
  }
}
